import { useState, useRef, useCallback, type CSSProperties } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Pencil,
  Trash2,
  Plus,
  FolderPlus,
  MoreVertical,
  GripVertical,
  EyeOff,
  Eye,
  UserRound,
} from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { TodoItemRow } from './TodoItemRow';
import { useTodoListContext } from '../context';
import type { TodoSectionResponse, TodoItemResponse } from '@/lib/api';

interface SectionCardProps {
  section: TodoSectionResponse;
  sections: TodoSectionResponse[];
  items: TodoItemResponse[];
}

export function SectionCard({ section, sections, items }: SectionCardProps) {
  const {
    isOwner,
    currentPrincipalId,
    visibleSectionIds,
    onEditSection,
    onDeleteSection,
    onCreateItem,
    onCreateSection,
    onReorderItems,
    onReorderSections,
    onToggleHiddenFromPublic,
    onAssignSection,
    onUnassignSection,
    assignableMembers,
    resolvePrincipalName,
  } = useTodoListContext();
  const [expanded, setExpanded] = useState(true);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [quickAddActive, setQuickAddActive] = useState(false);
  const [quickAddTitle, setQuickAddTitle] = useState('');
  const quickAddRef = useRef<HTMLInputElement>(null);
  const [quickAddCategoryActive, setQuickAddCategoryActive] = useState(false);
  const [quickAddCategoryName, setQuickAddCategoryName] = useState('');
  const quickAddCategoryRef = useRef<HTMLInputElement>(null);

  const isTimeframe = section.sectionType === 'timeframe';
  const childSections = sections
    .filter((s) => s.parentSectionId === section.sectionId)
    .filter((s) => !visibleSectionIds || visibleSectionIds.has(s.sectionId))
    .sort((a, b) => a.sortOrder - b.sortOrder);
  const sectionItems = items
    .filter((i) => i.sectionId === section.sectionId)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  const itemIds = sectionItems.map((i) => i.itemId);
  const childSectionIds = childSections.map((s) => s.sectionId);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
  );

  const handleItemDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = sectionItems.findIndex((i) => i.itemId === String(active.id));
      const newIndex = sectionItems.findIndex((i) => i.itemId === String(over.id));
      if (oldIndex !== -1 && newIndex !== -1) {
        const reordered = arrayMove(sectionItems, oldIndex, newIndex);
        onReorderItems?.(
          section.sectionId,
          reordered.map((i) => i.itemId),
        );
      }
    },
    [sectionItems, section.sectionId, onReorderItems],
  );

  const handleChildSectionDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = childSections.findIndex((s) => s.sectionId === String(active.id));
      const newIndex = childSections.findIndex((s) => s.sectionId === String(over.id));
      if (oldIndex !== -1 && newIndex !== -1) {
        const reordered = arrayMove(childSections, oldIndex, newIndex);
        onReorderSections?.(reordered.map((s) => s.sectionId));
      }
    },
    [childSections, onReorderSections],
  );

  // A manager (owner/admin/personal) can edit any section; a plain member can edit
  // only the sections assigned to them. Item edit rights are computed per item below.
  const isSectionAssignedToMe =
    !!currentPrincipalId && section.assignedToPrincipalId === currentPrincipalId;
  const canEditSection = isOwner || isSectionAssignedToMe;
  const canEditItemInSection = (item: TodoItemResponse): boolean =>
    isOwner || (!!currentPrincipalId && item.assignedToPrincipalId === currentPrincipalId);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: section.sectionId,
    disabled: !isOwner,
  });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={
        isTimeframe
          ? 'border-l-4 border-primary/40 rounded-r-lg bg-muted/30 p-3 mb-4'
          : 'border border-border rounded-lg bg-card p-3 mb-3'
      }
    >
      <div className="flex items-center gap-2 group">
        {isOwner && (
          <button
            type="button"
            className="cursor-grab touch-none text-muted-foreground/50 hover:text-muted-foreground"
            aria-label="Drag to reorder"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-4 w-4" />
          </button>
        )}

        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1.5 min-w-0 py-1 hover:text-primary transition-colors"
          aria-expanded={expanded}
          aria-label={`${expanded ? 'Collapse' : 'Expand'} section "${section.name}"`}
        >
          {expanded ? (
            <ChevronDown className="h-4 w-4 flex-shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 flex-shrink-0" />
          )}
          <span
            className={`truncate text-left ${
              isTimeframe ? 'text-base font-semibold' : 'text-sm font-medium text-muted-foreground'
            }`}
          >
            {section.name}
          </span>
          {section.hiddenFromPublic && (
            <span title="Hidden from public view">
              <EyeOff className="h-3 w-3 text-muted-foreground/50 flex-shrink-0" />
            </span>
          )}
        </button>

        {section.assignedToPrincipalId && resolvePrincipalName && (
          <Badge
            variant="outline"
            className="text-[10px] px-1.5 py-0 gap-1 text-muted-foreground flex-shrink-0"
            title={`Assigned to ${resolvePrincipalName(section.assignedToPrincipalId)}`}
          >
            <UserRound className="h-2.5 w-2.5" />
            {resolvePrincipalName(section.assignedToPrincipalId)}
          </Badge>
        )}

        {canEditSection && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 flex-shrink-0"
            onClick={() => {
              setQuickAddActive(true);
              setExpanded(true);
              setTimeout(() => quickAddRef.current?.focus(), 0);
            }}
            aria-label={`Add item to "${section.name}"`}
          >
            <Plus className="h-3.5 w-3.5" />
          </Button>
        )}

        {isOwner && isTimeframe && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 flex-shrink-0"
            onClick={() => {
              setQuickAddCategoryActive(true);
              setExpanded(true);
              setTimeout(() => quickAddCategoryRef.current?.focus(), 0);
            }}
            aria-label={`Add category to "${section.name}"`}
            title="Add category"
          >
            <FolderPlus className="h-3.5 w-3.5" />
          </Button>
        )}

        <div className="flex-1" />

        {canEditSection && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7 flex-shrink-0">
                <MoreVertical className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEditSection?.(section)}>
                <Pencil className="h-3.5 w-3.5 mr-2" />
                Edit
              </DropdownMenuItem>
              {isOwner && onAssignSection && assignableMembers && assignableMembers.length > 0 && (
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <UserRound className="h-3.5 w-3.5 mr-2" />
                    Assign to
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    <DropdownMenuRadioGroup
                      value={section.assignedToPrincipalId ?? ''}
                      onValueChange={(v) => {
                        if (v === '') onUnassignSection?.(section.sectionId);
                        else onAssignSection(section.sectionId, v);
                      }}
                    >
                      <DropdownMenuRadioItem value="">Unassigned</DropdownMenuRadioItem>
                      {assignableMembers.map((m) => (
                        <DropdownMenuRadioItem key={m.principalId} value={m.principalId}>
                          {m.label}
                        </DropdownMenuRadioItem>
                      ))}
                    </DropdownMenuRadioGroup>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              )}
              {isOwner && (
                <DropdownMenuItem
                  onClick={() =>
                    onToggleHiddenFromPublic?.(section.sectionId, !section.hiddenFromPublic)
                  }
                >
                  {section.hiddenFromPublic ? (
                    <>
                      <Eye className="h-3.5 w-3.5 mr-2" />
                      Show publicly
                    </>
                  ) : (
                    <>
                      <EyeOff className="h-3.5 w-3.5 mr-2" />
                      Hide from public
                    </>
                  )}
                </DropdownMenuItem>
              )}
              {isOwner && (
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={() => setConfirmDeleteOpen(true)}
                >
                  <Trash2 className="h-3.5 w-3.5 mr-2" />
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {expanded && (
        <div className="mt-1">
          {quickAddActive && (
            <div className="flex items-center gap-3 px-3 py-2">
              <div className="h-4 w-4 flex-shrink-0" />
              <Input
                ref={quickAddRef}
                value={quickAddTitle}
                onChange={(e) => setQuickAddTitle(e.target.value)}
                placeholder="New item title..."
                className="h-8 text-sm"
                onKeyDown={async (e) => {
                  if (e.key === 'Enter' && quickAddTitle.trim()) {
                    const title = quickAddTitle.trim();
                    setQuickAddActive(false);
                    setQuickAddTitle('');
                    await onCreateItem?.({ title, sectionId: section.sectionId });
                  }
                  if (e.key === 'Escape') {
                    setQuickAddActive(false);
                    setQuickAddTitle('');
                  }
                }}
                onBlur={async () => {
                  const title = quickAddTitle.trim();
                  setQuickAddActive(false);
                  setQuickAddTitle('');
                  if (title) {
                    await onCreateItem?.({ title, sectionId: section.sectionId });
                  }
                }}
              />
            </div>
          )}

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleItemDragEnd}
          >
            <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
              {sectionItems.map((item) => (
                <TodoItemRow
                  key={item.itemId}
                  item={item}
                  canEdit={canEditItemInSection(item)}
                  canManageSection={canEditSection}
                />
              ))}
            </SortableContext>
          </DndContext>

          {quickAddCategoryActive && (
            <div className="flex items-center gap-3 px-3 py-2">
              <FolderPlus className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
              <Input
                ref={quickAddCategoryRef}
                value={quickAddCategoryName}
                onChange={(e) => setQuickAddCategoryName(e.target.value)}
                placeholder="New category name..."
                className="h-8 text-sm"
                onKeyDown={async (e) => {
                  if (e.key === 'Enter' && quickAddCategoryName.trim()) {
                    const name = quickAddCategoryName.trim();
                    setQuickAddCategoryActive(false);
                    setQuickAddCategoryName('');
                    await onCreateSection?.({
                      name,
                      parentSectionId: section.sectionId,
                      sectionType: 'category',
                    });
                  }
                  if (e.key === 'Escape') {
                    setQuickAddCategoryActive(false);
                    setQuickAddCategoryName('');
                  }
                }}
                onBlur={async () => {
                  const name = quickAddCategoryName.trim();
                  setQuickAddCategoryActive(false);
                  setQuickAddCategoryName('');
                  if (name) {
                    await onCreateSection?.({
                      name,
                      parentSectionId: section.sectionId,
                      sectionType: 'category',
                    });
                  }
                }}
              />
            </div>
          )}

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleChildSectionDragEnd}
          >
            <SortableContext items={childSectionIds} strategy={verticalListSortingStrategy}>
              <div className="ml-4">
                {childSections.map((child) => (
                  <SectionCard
                    key={child.sectionId}
                    section={child}
                    sections={sections}
                    items={items}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>

          {sectionItems.length === 0 &&
            childSections.length === 0 &&
            !quickAddActive &&
            !quickAddCategoryActive && (
              <p className="text-xs text-muted-foreground px-3 py-2">No items in this section</p>
            )}
        </div>
      )}

      <Dialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete {isTimeframe ? 'Timeframe' : 'Section'}</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &ldquo;{section.name}&rdquo;? All items in this{' '}
              {isTimeframe ? 'timeframe' : 'section'} will be removed.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDeleteOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                onDeleteSection?.(section.sectionId);
                setConfirmDeleteOpen(false);
              }}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
